import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Table } from 'react-bootstrap';
import axios from '../../../../api/axios';
import { GlobalContext } from '../../../../store/GlobalContext';

type ReviewRow = {
  _id: string;
  productId: string;
  productName: string;
  customerName: string | null;
  customerEmail: string | null;
  rating: number;
  comment: string;
  isActive: boolean;
  isFlagged?: boolean;
  moderationReason?: string | null;
  moderatedBy?: string | null;
  moderatedAt?: string | null;
  createdAt: string;
};

const Reviews = () => {
  const { state } = useContext(GlobalContext);
  const token = state?.auth?.token;

  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [moderationBusyId, setModerationBusyId] = useState<string | null>(null);
  const [includeHidden, setIncludeHidden] = useState(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [ratingFilter, setRatingFilter] = useState('all');

  const [avgRating, setAvgRating] = useState(0);

  const params = useMemo(() => {
    const q: Record<string, string | number | boolean> = {
      page,
      limit,
      sortBy: 'createdAt',
      sortOrder: 'desc',
      includeInactive: includeHidden,
    };

    if (search.trim()) q.search = search.trim();
    if (ratingFilter !== 'all') q.rating = Number(ratingFilter);

    return q;
  }, [page, limit, search, ratingFilter, includeHidden]);

  const moderateReview = async (reviewId: string, action: 'hide' | 'unhide' | 'flag' | 'unflag') => {
    if (!token) return;

    let reason = '';
    if (action === 'hide' || action === 'flag') {
      reason = window.prompt('Optional moderation reason:', '') || '';
    }

    setError('');
    setSuccess('');
    setModerationBusyId(reviewId);
    try {
      await axios.put(
        `/products/admin/reviews/${reviewId}/moderation`,
        { action, reason: reason.trim() || undefined },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setSuccess(`Review ${action} action applied.`);

      const response = await axios.get('/products/admin/reviews', {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      const list = Array.isArray(response.data?.reviews) ? response.data.reviews : [];
      setRows(list);
      setTotal(Number(response.data?.total ?? list.length));
      setTotalPages(Number(response.data?.totalPages ?? 0));
      setAvgRating(Number(response.data?.summary?.avgRating ?? 0));
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to moderate review.');
    } finally {
      setModerationBusyId(null);
    }
  };

  useEffect(() => {
    const fetchReviews = async () => {
      if (!token) return;
      setLoading(true);
      setError('');
      try {
        const response = await axios.get('/products/admin/reviews', {
          headers: { Authorization: `Bearer ${token}` },
          params,
        });

        const list = Array.isArray(response.data?.reviews) ? response.data.reviews : [];
        setRows(list);
        setTotal(Number(response.data?.total ?? list.length));
        setTotalPages(Number(response.data?.totalPages ?? 0));
        setAvgRating(Number(response.data?.summary?.avgRating ?? 0));
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load reviews.');
        setRows([]);
        setTotal(0);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [token, params]);

  const canGoPrev = page > 1;
  const canGoNext = totalPages > 0 && page < totalPages;

  return (
    <Card className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Product Reviews (Read-Only)</h4>
        <Badge bg="dark">Avg Rating: {avgRating.toFixed(2)}</Badge>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <div className="d-flex flex-wrap gap-2 mb-3">
        <Form
          className="d-flex gap-2 flex-grow-1"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setSearch(searchInput);
          }}
          style={{ minWidth: '260px' }}
        >
          <Form.Control
            placeholder="Search by product/customer/comment"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
          <Button type="submit" variant="outline-dark">Search</Button>
          <Button
            type="button"
            variant="outline-secondary"
            onClick={() => {
              setSearchInput('');
              setSearch('');
              setPage(1);
            }}
          >
            Clear
          </Button>
        </Form>

        <Form.Select
          value={ratingFilter}
          onChange={(event) => {
            setPage(1);
            setRatingFilter(event.target.value);
          }}
          style={{ maxWidth: '180px' }}
        >
          <option value="all">All Ratings</option>
          <option value="5">5 Stars</option>
          <option value="4">4 Stars</option>
          <option value="3">3 Stars</option>
          <option value="2">2 Stars</option>
          <option value="1">1 Star</option>
        </Form.Select>

        <Form.Check
          type="switch"
          id="include-hidden-reviews"
          label="Include hidden"
          checked={includeHidden}
          onChange={(event) => {
            setPage(1);
            setIncludeHidden(event.target.checked);
          }}
        />

        <Form.Select
          value={limit}
          onChange={(event) => {
            setPage(1);
            setLimit(Number(event.target.value));
          }}
          style={{ maxWidth: '130px' }}
        >
          <option value={10}>10 / page</option>
          <option value={20}>20 / page</option>
          <option value={50}>50 / page</option>
        </Form.Select>
      </div>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>#</th>
            <th>Product</th>
            <th>Customer</th>
            <th>Rating</th>
            <th>Status</th>
            <th>Comment</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {!loading && rows.length === 0 ? (
            <tr>
              <td colSpan={8} className="text-center">No reviews found.</td>
            </tr>
          ) : (
            rows.map((item, index) => (
              <tr key={item._id}>
                <td>{(page - 1) * limit + index + 1}</td>
                <td>{item.productName}</td>
                <td>
                  <div>{item.customerName || 'Customer'}</div>
                  <small className="text-muted">{item.customerEmail || '-'}</small>
                </td>
                <td>{item.rating} / 5</td>
                <td>
                  {item.isActive ? <Badge bg="success" className="me-1">Visible</Badge> : <Badge bg="secondary" className="me-1">Hidden</Badge>}
                  {item.isFlagged ? <Badge bg="warning" text="dark">Flagged</Badge> : <Badge bg="light" text="dark">Clean</Badge>}
                </td>
                <td>
                  <div>{item.comment || '-'}</div>
                  {item.moderationReason ? <small className="text-muted">Reason: {item.moderationReason}</small> : null}
                </td>
                <td>{item.createdAt ? new Date(item.createdAt).toLocaleString() : '-'}</td>
                <td>
                  <div className="d-flex gap-1 flex-wrap">
                    <Button
                      size="sm"
                      variant={item.isActive ? 'outline-secondary' : 'outline-success'}
                      disabled={moderationBusyId === item._id}
                      onClick={() => moderateReview(item._id, item.isActive ? 'hide' : 'unhide')}
                    >
                      {item.isActive ? 'Hide' : 'Unhide'}
                    </Button>
                    <Button
                      size="sm"
                      variant={item.isFlagged ? 'outline-success' : 'outline-warning'}
                      disabled={moderationBusyId === item._id}
                      onClick={() => moderateReview(item._id, item.isFlagged ? 'unflag' : 'flag')}
                    >
                      {item.isFlagged ? 'Unflag' : 'Flag'}
                    </Button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <div className="d-flex justify-content-between align-items-center">
        <small>Showing {rows.length} of {total} reviews</small>
        <div className="d-flex align-items-center gap-2">
          <Button size="sm" variant="outline-secondary" disabled={!canGoPrev} onClick={() => setPage((prev) => prev - 1)}>
            Prev
          </Button>
          <span>Page {page}{totalPages > 0 ? ` of ${totalPages}` : ''}</span>
          <Button size="sm" variant="outline-secondary" disabled={!canGoNext} onClick={() => setPage((prev) => prev + 1)}>
            Next
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default Reviews;
